// material-ui
import { Box, Typography } from '@mui/material';
import { useEffect, useMemo, useCallback, useState } from 'react';
import { useLocation } from 'react-router-dom';

// project imports
import NavGroup from './NavGroup';
import menuItem from 'menu-items';
import { get_user } from 'utils/functions';
import { usePermissions } from 'utils/usePermissions';
import { useSidebarLayout } from './useSidebarLayout';

// Memoize static menu structure
const menuItemsMap = new Map();
menuItem.items.forEach((item) => menuItemsMap.set(item.id, item));

// Spacers, not real sections — they must never get a collapsible header.
const isSpacer = (id) => id === 'emptyAdmin' || id === 'emptyTrainer';

// ==============================|| SIDEBAR MENU LIST ||============================== //

const MenuList = () => {
  const { hasPermission, updateCounter } = usePermissions();
  const userData = useMemo(() => get_user(), []); // Memoize user data
  const { pathname } = useLocation();
  const [dragId, setDragId] = useState(null);
  const [overId, setOverId] = useState(null);
  // Reordering a LINK inside a section. Scoped by group id so a drag started in
  // one section cannot land in another — the saved order is per section, and a
  // cross-section drop would silently do nothing.
  const [itemDragId, setItemDragId] = useState(null);
  const [itemOverId, setItemOverId] = useState(null);

  // Memoize permission check for children
  const checkChildPermissions = useCallback(
    (child) => {
      // A link may narrow its section: the section decides who sees the heading,
      // this decides who sees the link. Used where one entry reaches a page with
      // a stricter gate than the rest of its section.
      if (child.userType?.length > 0 && !child.userType.includes(userData?.userType)) return false;
      if (!child.permissions || child.permissions.length === 0) return true;
      return child.permissions.some((permission) => hasPermission(permission));
    },
    [hasPermission, userData]
  );

  // The groups this user may actually see, after permission filtering.
  const visibleGroups = useMemo(() => {
    const out = [];
    menuItemsMap.forEach((item) => {
      if (item.type === 'empty') {
        out.push(item);
        return;
      }
      if (item.type !== 'group') return;
      if (!userData?.userType || !item?.userType?.includes(userData.userType)) return;

      // Super Admin bypass
      if (userData.userType === 1) {
        out.push(item);
        return;
      }
      if (!item.children) return;
      if (item.permissions?.length > 0 && !item.permissions.some((permission) => hasPermission(permission))) return;
      const authorizedChildren = item.children.filter(checkChildPermissions);
      if (!authorizedChildren.length) return;
      out.push({ ...item, children: authorizedChildren });
    });
    return out;
  }, [userData, hasPermission, checkChildPermissions, updateCounter]);

  // Which section holds the page being viewed. Matched on the longest url so
  // '/plan-finder-rules' cannot be claimed by '/plan-finder'.
  const activeGroupId = useMemo(() => {
    let best = null;
    let bestLen = 0;
    visibleGroups.forEach((g) => {
      (g.children || []).forEach((c) => {
        const url = c?.url;
        if (!url || url === '/') return;
        if ((pathname === url || pathname.startsWith(url + '/')) && url.length > bestLen) {
          best = g.id;
          bestLen = url.length;
        }
      });
    });
    return best;
  }, [visibleGroups, pathname]);

  const groupIds = useMemo(() => visibleGroups.map((g) => g.id), [visibleGroups]);
  const { orderedIds, isOpen, toggle, move, orderItems, moveItem, reset, isCustomised } = useSidebarLayout(groupIds, activeGroupId);

  const ordered = useMemo(() => {
    const byId = new Map(visibleGroups.map((g) => [g.id, g]));
    // Spacer groups are dropped entirely rather than rendered.
    //
    // 'emptyAdmin' / 'emptyTrainer' hold blank items ('empty11', 'empty12') that existed
    // only to pad the bottom of the rail. They were invisible while items without an icon
    // rendered nothing much; now that every item carries a dot, they showed up as two
    // stray dots under the last section. Padding is padding — the rail adds it in
    // Sidebar/index.js instead of faking it with menu entries.
    return orderedIds.map((id) => byId.get(id)).filter((g) => g && !isSpacer(g.id));
  }, [orderedIds, visibleGroups]);

  // Debug re-renders
  useEffect(() => {}, [updateCounter]);

  return (
    <>
      {ordered.map((item) => {
        const children = orderItems(item.id, item.children || []);
        return (
          <NavGroup
            key={item.id}
            item={item}
            collapsible
            open={isOpen(item.id)}
            onToggle={() => toggle(item.id)}
            orderedChildren={children}
            itemDrag={{
              dragId: itemDragId?.groupId === item.id ? itemDragId.id : null,
              overId: itemOverId?.groupId === item.id ? itemOverId.id : null,
              setDragId: (id) => setItemDragId({ groupId: item.id, id }),
              setOverId: (id) => setItemOverId({ groupId: item.id, id }),
              onDrop: (targetId) => {
                if (itemDragId?.groupId === item.id) {
                  moveItem(item.id, itemDragId.id, targetId, item.children || []);
                }
                setItemDragId(null);
                setItemOverId(null);
              },
              onDragEnd: () => {
                setItemDragId(null);
                setItemOverId(null);
              }
            }}
            isDragging={dragId === item.id}
            isDropTarget={overId === item.id && dragId !== item.id}
            dragHandlers={{
              draggable: dragId === item.id,
              onGripMouseDown: () => setDragId(item.id),
              onDragStart: (e) => {
                e.dataTransfer.effectAllowed = 'move';
                // Firefox refuses to start a drag without data set.
                e.dataTransfer.setData('text/plain', item.id);
              },
              onDragOver: (e) => {
                if (!dragId || dragId === item.id) return;
                e.preventDefault();
                setOverId(item.id);
              },
              onDrop: (e) => {
                e.preventDefault();
                move(dragId, item.id);
                setDragId(null);
                setOverId(null);
              },
              onDragEnd: () => {
                setDragId(null);
                setOverId(null);
              }
            }}
          />
        );
      })}

      {/* Only offered once the order has actually been changed — a permanent "reset"
          control on a default sidebar is just clutter. */}
      {isCustomised && (
        <Box sx={{ px: 1.5, pt: 1, pb: 2 }}>
          <Typography
            component="button"
            type="button"
            onClick={reset}
            sx={{
              border: 0,
              background: 'transparent',
              p: 0,
              font: 'inherit',
              fontSize: '0.75rem',
              color: 'text.secondary',
              cursor: 'pointer',
              '&:hover': { color: 'text.primary', textDecoration: 'underline' },
              '&:focus-visible': { outline: '2px solid', outlineColor: 'accent.text', outlineOffset: '2px' }
            }}
          >
            Reset menu order
          </Typography>
        </Box>
      )}
    </>
  );
};

export default MenuList;
